import React, { useState, useContext, useEffect } from 'react';
import { ArrowLeft, Camera, Clock, AlertCircle, Check, AlertTriangle, Loader2, ChevronLeft } from 'lucide-react';
import { saveProductData } from './productService';
import { getSubscriptionDurations, formatDurationsForUI } from './durationService';
import { useNavigate, useParams } from 'react-router-dom';
import AppContext from '../../context/AppContext';

const AjouterProduit = () => {
  const navigate = useNavigate();
  const { idGroup } = useParams();
  const { id, setupTelegramBackButton } = useContext(AppContext);
  
  // Form state
  const [formData, setFormData] = useState({
    titre: '',
    prix: '',
    description: '',
    image: null,
    duree: ''
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState({ visible: false, type: '', message: '' });
  const [durations, setDurations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Set up Telegram back button and clean up
  useEffect(() => {
    const backButtonShown = setupTelegramBackButton(true, () => navigate(-1));
    
    return () => {
      // Nettoyer le back button Telegram
      if (backButtonShown) {
        setupTelegramBackButton(false);
      }
      
      // Nettoyer les URL d'objets si créés via URL.createObjectURL
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [navigate, setupTelegramBackButton, imagePreview]);

  // Load subscription durations
  useEffect(() => {
    const loadDurations = async () => {
      try {
        setLoading(true);
        const durationsData = await getSubscriptionDurations();
        const formattedDurations = formatDurationsForUI(durationsData);
        setDurations(formattedDurations);
      } catch (error) {
        console.error("Error loading durations:", error);
        setAlert({
          visible: true,
          type: 'error',
          message: 'Impossible de charger les durées disponibles'
        });
      } finally {
        setLoading(false);
      }
    };
    
    loadDurations();
  }, []);

  // Détecter si nous sommes sur un appareil mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  // Handle image selection - version simplifiée sans optimisation complexe
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier la taille du fichier (max 5MB sur desktop, 3MB sur mobile)
      const maxSize = isMobile ? 3 * 1024 * 1024 : 5 * 1024 * 1024;
      if (file.size > maxSize) {
        setAlert({
          visible: true,
          type: 'error',
          message: `L'image est trop volumineuse. La taille maximale est de ${isMobile ? '3' : '5'} Mo.`
        });
        return;
      }
      
      try {
        // Utiliser simplement createObjectURL pour l'aperçu, sans traitement complexe
        const objectUrl = URL.createObjectURL(file);
        
        // Définir directement l'aperçu et les données du formulaire
        setImagePreview(objectUrl);
        setFormData({ ...formData, image: file });
        
        // Effacer les alertes d'erreur si présentes
        if (alert.type === 'error' || alert.type === 'loading') {
          setAlert({ visible: false });
        }
      } catch (error) {
        console.error("Erreur lors de la création de l'aperçu:", error);
        
        // En cas d'erreur, utiliser quand même le fichier sans aperçu
        setFormData({ ...formData, image: file });
        setAlert({
          visible: true,
          type: 'error',
          message: "Problème avec l'aperçu de l'image, mais vous pouvez continuer."
        });
      }
    }
  };

  // Handle form submission - version simplifiée
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.titre.trim() || !formData.prix || !formData.duree || !formData.image) {
      const missingFields = [
        !formData.titre.trim() ? 'Titre' : '',
        !formData.prix ? 'Prix' : '',
        !formData.duree ? 'Durée' : '',
        !formData.image ? 'Image' : ''
      ].filter(Boolean).join(', ');
      
      setAlert({
        visible: true,
        type: 'error',
        message: `Veuillez compléter tous les champs requis: ${missingFields}`
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Afficher une alerte simple pour l'upload
      setAlert({
        visible: true,
        type: 'loading',
        message: 'Publication en cours...'
      });
      
      // Sauvegarder les données du produit directement
      const result = await saveProductData(formData, idGroup, id);

      if (result.success) {
        // Montrer un message de succès
        setAlert({
          visible: true,
          type: 'success',
          message: 'Produit publié avec succès!'
        });
        
        // Réinitialiser le formulaire
        setFormData({
          titre: '',
          prix: '',
          description: '',
          image: null,
          duree: ''
        });
        setImagePreview(null);
        
        // Nettoyer l'URL de l'aperçu si elle existe
        if (imagePreview && imagePreview.startsWith('blob:')) {
          URL.revokeObjectURL(imagePreview);
        }
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout du produit:', error);
      setAlert({
        visible: true,
        type: 'error',
        message: error.message || 'Erreur lors de la publication. Veuillez réessayer.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Extract tier color for UI
  const getTierColor = (tier) => {
    switch(tier) {
      case 'premium': return 'bg-purple-500';
      case 'standard': return 'bg-blue-500';
      case 'basic': return 'bg-green-500';
      case 'test': return 'bg-amber-500';
      default: return 'bg-gray-500';
    }
  };

  // Handle modal close
  const handleCloseAlert = () => {
    setAlert({ visible: false, type: '', message: '' });
    if (alert.type === 'success') {
      // Naviguer vers la page des produits au lieu de simplement revenir en arrière
      navigate(`/ProductsList`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 pb-24 relative">
      {/* Header */}
      <div className="bg-white fixed top-0 left-0 right-0 z-10 shadow-md">
        <div className="px-4 py-4 flex items-center">
          <h1 className="text-xl font-semibold text-center flex-1">Nouveau produit</h1>
        </div>
      </div>

      {/* Central Alert Modal */}
      {alert.visible && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleCloseAlert}></div>
          <div className={`relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-auto overflow-hidden transform transition-all`}>
            <div className={`p-2 ${
              alert.type === 'error' ? 'bg-red-500' : 
              alert.type === 'success' ? 'bg-green-500' : 
              'bg-blue-500'
            }`}></div>
            
            <div className="p-6">
              <div className="flex items-start mb-4">
                <div className={`p-3 rounded-full mr-4 ${
                  alert.type === 'error' ? 'bg-red-100' : 
                  alert.type === 'success' ? 'bg-green-100' : 
                  'bg-blue-100'
                }`}>
                  {alert.type === 'error' ? (
                    <AlertTriangle className={`w-8 h-8 text-red-500`} />
                  ) : alert.type === 'success' ? (
                    <Check className={`w-8 h-8 text-green-500`} />
                  ) : (
                    <Loader2 className={`w-8 h-8 text-blue-500 animate-spin`} />
                  )}
                </div>
                <div>
                  <h3 className="font-bold text-xl text-gray-900">
                    {alert.type === 'error' ? 'Erreur' : 
                     alert.type === 'success' ? 'Succès' : 
                     'En cours...'}
                  </h3>
                  <p className="text-gray-700 mt-1">
                    {alert.message}
                  </p>
                </div>
              </div>
              
              {(alert.type === 'error' || alert.type === 'success') && (
                <button
                  onClick={handleCloseAlert}
                  className={`w-full py-3 px-4 rounded-xl font-medium text-white transition-all ${
                    alert.type === 'error' ? 'bg-red-500 hover:bg-red-600' : 
                    'bg-green-500 hover:bg-green-600'
                  } flex items-center justify-center gap-2`}
                >
                  {alert.type === 'success' ? (
                    <>
                      <ChevronLeft className="w-5 h-5" />
                      Retourner aux produits
                    </>
                  ) : (
                    'Compris'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <main className="px-4 pt-20 pb-32">
        <div className="space-y-6">
          {/* Image Section */}
          <div className="bg-white rounded-2xl p-6 shadow-md">
            <label className="block text-gray-800 font-medium mb-3 text-lg">
              Photo du produit ici <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              onChange={handleImageChange}
              accept="image/*"
              className="hidden"
              id="image-upload"
            />
            <label
              htmlFor="image-upload"
              className="block touch-manipulation w-full"
            >
              <div className={`relative rounded-xl overflow-hidden border-3 border-dashed
                ${imagePreview ? 'border-blue-300' : 'border-gray-300'}
                hover:border-blue-500 transition-colors`}>
                {imagePreview ? (
                  <div className="aspect-square w-full">
                    <img
                      src={imagePreview}
                      alt="Prévisualisation"
                      className="w-full h-full object-cover rounded-xl"
                    />
                    <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors flex items-center justify-center">
                      <div className="absolute bottom-4 right-4 bg-blue-600 p-3 rounded-full shadow-lg">
                        <Camera className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="aspect-square w-full bg-blue-50 rounded-xl flex flex-col items-center justify-center p-8 hover:bg-blue-100 transition-colors">
                    <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mb-4 shadow-inner">
                      <Camera className="w-12 h-12 text-blue-600" />
                    </div>
                    <p className="text-gray-800 font-semibold text-lg">Ajouter une photo</p>
                    <p className="text-gray-600 text-base text-center mt-2">Cliquez pour choisir une image</p>
                  </div>
                )}
              </div>
            </label>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Title */}
            <div className="bg-white rounded-2xl shadow-md overflow-hidden">
              <label className="block text-gray-800 font-semibold px-6 pt-5 text-lg">
                Titre <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Ex: iPhone 12 Pro Max"
                className="w-full px-6 py-5 text-lg text-gray-800 outline-none border-b-2 border-gray-200 focus:border-blue-500 bg-white hover:bg-blue-50 transition-colors"
                value={formData.titre}
                onChange={(e) => setFormData({...formData, titre: e.target.value})}
              />
            </div>

            {/* Price */}
            <div className="bg-white rounded-2xl shadow-md overflow-hidden">
              <label className="block text-gray-800 font-semibold px-6 pt-5 text-lg">
                Prix (FCFA) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  inputMode="decimal"
                  pattern="[0-9]*"
                  placeholder="0"
                  className="w-full px-6 py-5 text-lg text-gray-800 outline-none border-b-2 border-gray-200 focus:border-blue-500 bg-white hover:bg-blue-50 transition-colors"
                  value={formData.prix}
                  onChange={(e) => setFormData({...formData, prix: e.target.value})}
                />
              </div>
            </div>

            {/* Duration */}
            <div className="bg-white rounded-2xl p-6 shadow-md space-y-5">
              <label className="flex items-center gap-2 text-gray-800 font-semibold text-lg">
                <Clock className="w-6 h-6 text-blue-600" />
                Durée d'abonnement <span className="text-red-500">*</span>
              </label>
              
              {loading ? (
                <div className="py-10 flex justify-center">
                  <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {durations.map((duration) => (
                    <div 
                      key={duration.id}
                      onClick={() => setFormData({...formData, duree: duration.value})}
                      className={`relative p-5 rounded-xl border-3 transition-all cursor-pointer shadow-sm hover:shadow-md
                        ${formData.duree === duration.value 
                          ? 'border-blue-500 bg-blue-50 shadow-md' 
                          : 'border-gray-200 hover:border-blue-300'}`}
                    >
                      {duration.discount > 0 && (
                        <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2.5 py-1.5 rounded-full shadow-sm">
                          -{duration.discount}%
                        </div>
                      )}
                      <div className="flex flex-col">
                        <div className="flex items-center space-x-2 mb-2">
                          <div className={`w-3 h-3 rounded-full ${getTierColor(duration.priceTier)}`}></div>
                          <span className="font-semibold text-gray-900 text-lg">{duration.label}</span>
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-1">{duration.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Description */}
            <div className="bg-white rounded-2xl shadow-md overflow-hidden">
              <label className="block text-gray-800 font-semibold px-6 pt-5 text-lg">Description</label>
              <textarea
                placeholder="Décrivez votre produit en détail..."
                className="w-full px-6 py-5 text-lg text-gray-800 outline-none border-b-2 border-gray-200 focus:border-blue-500 min-h-[150px] resize-none hover:bg-blue-50 transition-colors"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                maxLength={500}
              />
              <div className="px-6 py-3 text-sm text-gray-600 text-right">
                {formData.description.length}/500
              </div>
            </div>
          </form>
        </div>
      </main>

      {/* Submit Button */}
      <div className="fixed bottom-0 left-0 right-0 p-5 bg-white border-t border-gray-200 shadow-lg">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting || !formData.titre || !formData.prix || !formData.duree || !formData.image}
          className={`w-full py-5 rounded-xl font-bold text-lg transition-all shadow-md
                    ${(!formData.titre || !formData.prix || !formData.duree || !formData.image || isSubmitting)
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]'}`}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-3">
              <Loader2 className="w-6 h-6 animate-spin" />
              Publication en cours...
            </span>
          ) : (
            'Publier l\'annonce'
          )}
        </button>
      </div>
    </div>
  );
};

export default AjouterProduit;